import classNames from 'classnames';
import * as React from 'react';

import styles from './Button.module.css';

type ButtonAlign =
    | {
          fullWidth: true;
          align?: 'left' | 'center' | 'right';
      }
    | { fullWidth?: false; align?: never };

type ButtonProps = {
    size?:
        | 'xxsmall'
        | 'xsmall'
        | 'small'
        | 'medium'
        | 'medium-narrow'
        | 'large'
        | 'xlarge';
    variant?:
        | 'default'
        | 'brand'
        | 'subtle'
        | 'outline'
        | 'white'
        | 'text'
        | 'destructive'
        | 'text-translucent-hover';
    style?: 'default' | 'pill';
    fontWeight?: 'auto' | 'normal' | 'bold';
    fake?: boolean;
    noRoundCorners?: boolean;
    buttonRef?: React.Ref<HTMLButtonElement>;
} & ButtonAlign &
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'size' | 'style'>;

const Button = ({
    size = 'medium',
    variant = 'default',
    type = 'button',
    style = 'default',
    className,
    children,
    fullWidth = false,
    fake = false,
    noRoundCorners = false,
    fontWeight = 'auto',
    align,
    buttonRef,
    ...buttonProps
}: ButtonProps) => {
    const classes = classNames(styles.Root, className, {
        [styles.sizeXxSmall]: size === 'xxsmall',
        [styles.sizeXSmall]: size === 'xsmall',
        [styles.sizeSmall]: size === 'small',
        [styles.sizeMedium]: size === 'medium',
        [styles.sizeMediumNarrow]: size === 'medium-narrow',
        [styles.sizeLarge]: size === 'large',
        [styles.sizeXlarge]: size === 'xlarge',
        [styles.variantDefault]: variant === 'default',
        [styles.variantBrand]: variant === 'brand',
        [styles.variantSubtle]: variant === 'subtle',
        [styles.variantOutline]: variant === 'outline',
        [styles.variantWhite]: variant === 'white',
        [styles.variantDestructive]: variant === 'destructive',
        [styles.variantText]: variant === 'text',
        [styles.variantTextTranslucentHover]:
            variant === 'text-translucent-hover',
        [styles.stylePill]: style === 'pill',
        [styles.fullWidth]: fullWidth,
        [styles.alignLeft]: align === 'left',
        [styles.alignCenter]: align === 'center',
        [styles.alignRight]: align === 'right',
        [styles.fontWeightNormal]: fontWeight === 'normal',
        [styles.fontWeightBold]: fontWeight === 'bold',
        [styles.noRoundCorners]: noRoundCorners,
    });

    if (fake) {
        return (
            <span className={classes}>
                <span className={styles.Content}>{children}</span>
            </span>
        );
    }

    return (
        <button
            {...buttonProps}
            className={classes}
            type={type}
            ref={buttonRef}
        >
            <span className={styles.Content}>{children}</span>
        </button>
    );
};

export default Button;
